import type React from "react";
import { useRef } from "react";
import { StyleSheet, View, TextInput } from "react-native";
import { Typography, FormikTextInputField } from "@smartrent/ui";

import { useScript } from "./useScript";
import type { GooglePlacesAutocompleteProps } from "./types";
import { usePlacesAutocomplete } from "./usePlacesAutocomplete";
import { PlacesSuggestionsDropdown } from "./PlacesSuggestionsDropdown";

export type { GooglePlacesSelectedPlace } from "./types";

declare global {
  interface Window {
    google?: typeof google;
  }
}

interface GooglePlacesAutocompleteInnerProps
  extends GooglePlacesAutocompleteProps {
  mapsScriptLoaded: boolean;
}

const GooglePlacesAutocompleteInner: React.FC<
  GooglePlacesAutocompleteInnerProps
> = ({
  name,
  label = "Address",
  placeholder = "",
  onPlaceSelected,
  cityFieldName,
  stateFieldName,
  zipFieldName,
  testID,
  mapsScriptLoaded,
}) => {
  const listId = `${name}-suggestions`;
  const containerRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const { suggestions, listOpen, applyPrediction, mapsLoading } =
    usePlacesAutocomplete({
      mapsScriptLoaded,
      name,
      cityFieldName,
      stateFieldName,
      zipFieldName,
      onPlaceSelected,
      inputRef,
      containerRef,
    });

  return (
    <View ref={containerRef} style={styles.wrapper}>
      <FormikTextInputField
        ref={inputRef}
        name={name}
        label={label}
        textInputProps={{ placeholder }}
        {...(testID ? { testID } : {})}
      />
      {mapsLoading && (
        <Typography variation="regular.caption.medium" color="secondary">
          Loading address autocomplete… You can still enter the address
          manually.
        </Typography>
      )}
      {!mapsLoading && (
        <PlacesSuggestionsDropdown
          suggestions={suggestions}
          open={listOpen}
          onSelect={applyPrediction}
          listId={listId}
          testID={testID}
        />
      )}
    </View>
  );
};

export const GooglePlacesAutocomplete: React.FC<
  GooglePlacesAutocompleteProps
> = (props) => {
  const apiKey = import.meta.env.REACT_APP_GOOGLE_MAPS_FE_API_KEY || "";
  const src = apiKey
    ? `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=weekly`
    : "";
  const [loaded, loadError] = useScript(src);

  if (loadError || !apiKey) {
    return (
      <View>
        <FormikTextInputField
          name={props.name}
          label={props.label || "Address"}
          textInputProps={{ placeholder: props.placeholder || "" }}
          {...(props.testID ? { testID: props.testID } : {})}
        />
        <Typography variation="regular.caption.medium" color="warning">
          Address autocomplete unavailable. Enter address manually.
        </Typography>
      </View>
    );
  }

  return (
    <GooglePlacesAutocompleteInner
      {...props}
      mapsScriptLoaded={Boolean(loaded)}
    />
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    position: "relative",
    zIndex: 50,
  },
});
